import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { Popup } from 'semantic-ui-react';
import { SidebarNavItem } from 'common/types/SidebarNavItem';

const isLocalEnv = process.env.NODE_ENV === 'development';
// Vite always builds with NODE_ENV=production, so the deployment target is
// signalled separately via REACT_APP_ENVIRONMENT (set per Amplify branch).
const isStagingEnv = process.env.REACT_APP_ENVIRONMENT === 'staging';
const sidebarSelectedBackground = isLocalEnv
  ? 'rgba(125, 192, 140, 0.4)'
  : isStagingEnv
    ? 'rgba(253, 224, 71, 0.4)'
    : 'rgba(165, 207, 238, 0.4)';
const sidebarHoverBackground = isLocalEnv
  ? 'rgba(125, 192, 140, 0.3)'
  : isStagingEnv
    ? 'rgba(253, 224, 71, 0.3)'
    : 'rgba(165, 207, 238, 0.3)';
const sidebarSectionBackground = isLocalEnv
  ? 'rgba(125, 192, 140, 0.1)'
  : isStagingEnv
    ? 'rgba(253, 224, 71, 0.1)'
    : 'rgba(165, 207, 238, 0.1)';
const sidebarSeparatorColor = isLocalEnv
  ? 'rgba(125, 192, 140, 0.3)'
  : isStagingEnv
    ? 'rgba(245, 158, 11, 0.3)'
    : 'rgba(165, 207, 238, 0.3)';

interface NavItemProps {
  navItem: SidebarNavItem;
  isDisabled?: boolean;
  selected?: string;
  isFrench?: () => boolean;
  isOpen?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ navItem, isDisabled, selected, isFrench, isOpen }) => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(() => {
    // Auto-expand if any child is selected
    if (navItem.children) {
      return navItem.children.some(child => location.pathname.startsWith(child.link));
    }
    return false;
  });
  
  const isSelected = selected === navItem.link || location.pathname.startsWith(navItem.link);
  const isFrenchText = isFrench?.() || false;
  const displayName = isFrenchText && navItem.fr ? navItem.fr : navItem.title;
  const hasChildren = navItem.children && navItem.children.length > 0;

  const renderIcon = () => {
    if (!navItem.svg) {
      return navItem.svg ? <Icon name={navItem.svg} /> : null;
    }

    // Check if svg is a function
    if (typeof navItem.svg === 'function') {
      return navItem.svg(isDisabled ? '#DDDDDD' : isSelected ? '#1e2172' : '#666');
    }

    // If svg is a React element, render it directly
    return navItem.svg;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      {navItem.section_title && (
        <>
          <Section>
            <Separator />
            <SectionTitle isOpen={isOpen}>{isOpen ? navItem.section_title : '.'}</SectionTitle>
          </Section>
          <Separator />
        </>
      )}
      <Popup
        content={displayName}
        position="right center"
        trigger={
          <Link
            to={!isDisabled && !hasChildren ? navItem.link : '#'}
            data-testid={`nav-link-${navItem.id}`}
            target={navItem.target || '_self'}
            onClick={handleClick}
          >
            <ItemContainer isSelected={isSelected} isDisabled={isDisabled} isOpen={isOpen}>
              <IconContainer isSelected={isSelected} isDisabled={isDisabled}>
                {renderIcon()}
              </IconContainer>
              <Text isOpen={isOpen} isSelected={isSelected} isDisabled={isDisabled}>
                {displayName}
              </Text>
              {hasChildren && isOpen && (
                <ExpandIcon isExpanded={isExpanded}>
                  <Icon name={isExpanded ? 'chevron down' : 'chevron right'} />
                </ExpandIcon>
              )}
            </ItemContainer>
          </Link>
        }
        disabled={isOpen}
      />
      
      {/* Render children: show icons when collapsed, full list when expanded */}
      {hasChildren && (isOpen ? isExpanded : true) && (
        <ChildrenContainer isOpen={isOpen}>
          {navItem.children!.map((child) => {
            const childSelected = location.pathname === child.link;
            const childIcon = typeof child.svg === 'function'
              ? child.svg(isDisabled ? '#DDDDDD' : childSelected ? '#1e2172' : '#666')
              : child.svg;
            const childContent = (
              <ChildItemContainer
                isSelected={childSelected}
                isDisabled={isDisabled}
                isOpen={isOpen}
              >
                <ChildIconContainer isSelected={childSelected} isOpen={isOpen}>
                  {childIcon}
                </ChildIconContainer>
                <ChildText isSelected={childSelected} isOpen={isOpen}>
                  {isFrenchText && child.fr ? child.fr : child.title}
                </ChildText>
              </ChildItemContainer>
            );

            return (
              <ChildNavItem key={child.id}>
                {isOpen ? (
                  <Link to={!isDisabled ? child.link : '#'} data-testid={`nav-link-${child.id}`}>
                    {childContent}
                  </Link>
                ) : (
                  <Popup content={isFrenchText && child.fr ? child.fr : child.title} position="right center" trigger={
                    <Link to={!isDisabled ? child.link : '#'} data-testid={`nav-link-${child.id}`}>
                      {childContent}
                    </Link>
                  } />
                )}
              </ChildNavItem>
            );
          })}
        </ChildrenContainer>
      )}
    </>
  );
};

const ItemContainer = styled.div<{ isSelected: boolean; isDisabled?: boolean; isOpen?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 0px 8px 12px;
  margin: 2px 5px 0px 5px;

  cursor: ${props => (props.isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.isDisabled ? 0.5 : 1)};
  background: ${props => (props.isSelected ? sidebarSelectedBackground : 'transparent')};
  transition: background 0.2s ease-in-out;
  border-radius: 8px;

  &:hover {
    background: ${props => (props.isDisabled ? 'transparent' : sidebarHoverBackground)};
  }
`;

const IconContainer = styled.div<{ isSelected: boolean; isDisabled?: boolean }>`
  width: 24px;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: left;
  color: ${props => (props.isSelected ? '#166534' : '#666')};
  font-size: 1.1em;
  transition: color 0.3s ease-in-out;
`;

const Text = styled.div<{ isOpen?: boolean; isSelected: boolean; isDisabled?: boolean }>`
  font-family: 'Poppins', sans-serif;
  overflow: hidden;
  white-space: nowrap;
  margin-left: 8px;
  color: ${props => (props.isSelected ? '#333' : '#666')};
  font-size: 13px;
  font-weight: ${props => (props.isSelected ? '450' : '400')};
  opacity: ${props => (props.isOpen ? 1 : 0)};
  max-width: ${props => (props.isOpen ? '200px' : '0px')};
  transition:
    max-width 0.3s ease,
    opacity 0.2s ease;
`;

const Section = styled.div`
  margin: 8px 0px 0px 0px;
  background-color: ${sidebarSectionBackground};
`;

const Separator = styled.div`
  height: 1px;
  background-color: ${sidebarSeparatorColor};
  margin: 0px 2px 2px 0px;
  width: 100%;
`;

const SectionTitle = styled.div<{ isOpen?: boolean }>`
  font-size: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 550;
  white-space: nowrap;
  color: ${props => (props.isOpen ? '#666' : 'transparent')};
  margin: 1px 14px 1px 14px;
`;

const ExpandIcon = styled.div<{ isExpanded: boolean }>`
  margin-left: auto;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  color: #666;
  font-size: 12px;
`;

const ChildrenContainer = styled.div<{ isOpen?: boolean }>`
  margin-left: ${props => (props.isOpen ? '12px' : '0')};
  display: flex;
  /* Always stack children vertically; when collapsed, center them in the narrow sidebar */
  flex-direction: column;
  gap: ${props => (props.isOpen ? '0' : '6px')};
  align-items: ${props => (props.isOpen ? 'flex-start' : 'center')};
  /* Ensure children are visible and not clipped */
  z-index: 10;
`;

const ChildNavItem = styled.div`
  margin: 0;
`;

const ChildItemContainer = styled.div<{ isSelected: boolean; isDisabled?: boolean; isOpen?: boolean }>`
  display: flex;
  align-items: center;
  width: ${props => (props.isOpen ? '100%' : 'auto')};
  box-sizing: border-box;
  padding: ${props => (props.isOpen ? '6px 24px' : '6px')};
  margin: ${props => (props.isOpen ? '2px 0px 0px 0px' : '0')};
  justify-content: ${props => (props.isOpen ? 'flex-start' : 'center')};
  cursor: ${props => (props.isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.isDisabled ? 0.5 : 1)};
  background: ${props => (props.isSelected ? sidebarSelectedBackground : 'transparent')};
  transition: background 0.2s ease-in-out;
  border-radius: 8px;

  &:hover {
    background: ${props => (props.isDisabled ? 'transparent' : sidebarHoverBackground)};
  }
`;

const ChildIconContainer = styled.div<{ isSelected: boolean; isOpen?: boolean }>`
  width: ${props => (props.isOpen ? '20px' : '28px')};
  min-width: ${props => (props.isOpen ? '20px' : '28px')};
  height: ${props => (props.isOpen ? '20px' : '28px')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => (props.isSelected ? '#166534' : '#666')};
  font-size: ${props => (props.isOpen ? '0.9em' : '1.1em')};
  transition: color 0.3s ease-in-out;
`;

const ChildText = styled.div<{ isSelected: boolean; isOpen?: boolean }>`
  font-family: 'Poppins', sans-serif;
  overflow: hidden;
  white-space: nowrap;
  margin-left: 8px;
  color: ${props => (props.isSelected ? '#333' : '#666')};
  font-size: 12px;
  font-weight: ${props => (props.isSelected ? '450' : '400')};
  display: ${props => (props.isOpen ? 'block' : 'none')};
`;

export default NavItem;
